import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listGames, createGame, joinGame } from '../api/games';
import { useLobbyWebSocket } from '../hooks/useWebSocket';
import '../styles/pages.css';

const STATUS_LABEL = {
  waiting: '等待中', ready: '准备就绪',
  in_progress: '进行中', finished: '已结束',
};

export default function LobbyPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [myGames, setMyGames] = useState([]);
  const [filter, setFilter] = useState('waiting');
  const [creating, setCreating] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastLobbyEventAt = useRef(0);

  const fetchGames = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [allRes, myRes] = await Promise.all([
        listGames({ status: filter }),
        listGames({ my_games: true }),
      ]);
      setGames(allRes.data);
      setMyGames(myRes.data);
    } catch {
      setError('加载游戏列表失败');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  const handleLobbyMessage = useCallback((data) => {
    if (data.type !== 'lobby.update') return;

    const eventAt = Date.parse(data.event_at || '');
    if (eventAt && eventAt < lastLobbyEventAt.current) return;
    if (eventAt) lastLobbyEventAt.current = eventAt;

    fetchGames();
  }, [fetchGames]);

  const { closeCode } = useLobbyWebSocket(handleLobbyMessage, { onOpen: fetchGames });

  useEffect(() => {
    if (closeCode === 4001) {
      setError('大厅实时连接认证失败，请重新登录');
    }
  }, [closeCode]);

  const handleCreate = async () => {
    try {
      const { data } = await createGame(maxPlayers);
      navigate(`/games/${data.id}/room`);
    } catch (err) {
      setError(err.response?.data?.detail || '创建游戏失败');
    }
  };

  const handleJoin = async (gameId) => {
    try {
      await joinGame(gameId);
      navigate(`/games/${gameId}/room`);
    } catch (err) {
      setError(err.response?.data?.detail || '加入游戏失败');
    }
  };

  const handleResume = (g) => {
    if (g.status === 'in_progress') navigate(`/games/${g.id}/play`);
    else navigate(`/games/${g.id}/room`);
  };

  const activeMyGames = myGames.filter(g => g.status !== 'finished');

  return (
    <div className="lobby-page">
      <div className="lobby-header">
        <div className="lobby-header-left">
          <Link to="/home" className="back-link">← 游戏中心</Link>
          <h2>🎴 Splendor 大厅</h2>
        </div>
        <div className="lobby-header-right">
          <span className="username-badge">👤 {user?.username}</span>
          <button className="btn-secondary" onClick={logout}>退出登录</button>
        </div>
      </div>

      {error && <p className="error-msg">{error}</p>}

      {/* 我的进行中游戏 */}
      {activeMyGames.length > 0 && (
        <div className="section">
          <h3>我的游戏</h3>
          <div className="game-list">
            {activeMyGames.map(g => (
              <div key={g.id} className="game-card my-game">
                <div className="game-card-title">
                  <strong>房间 #{g.id}</strong>
                  <span className={`status-badge status-${g.status}`}>{STATUS_LABEL[g.status]}</span>
                </div>
                <div className="game-card-info">
                  {g.players.map(p => p.username).join('、')} &nbsp;
                  ({g.players.length}/{g.max_players} 人)
                </div>
                <button className="btn-primary" onClick={() => handleResume(g)}>
                  {g.status === 'in_progress' ? '继续游戏' : '进入房间'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 创建游戏 */}
      {creating ? (
        <div className="section create-game-form">
          <h3>创建新游戏</h3>
          <div className="form-group form-group-inline">
            <label>玩家人数：</label>
            {[2, 3, 4].map(n => (
              <button
                key={n}
                className={`btn-choice ${maxPlayers === n ? 'active' : ''}`}
                onClick={() => setMaxPlayers(n)}
              >{n} 人</button>
            ))}
          </div>
          <div className="btn-row">
            <button className="btn-primary" onClick={handleCreate}>创建</button>
            <button className="btn-secondary" onClick={() => setCreating(false)}>取消</button>
          </div>
        </div>
      ) : (
        <button className="btn-primary btn-create" onClick={() => setCreating(true)}>
          + 创建新游戏
        </button>
      )}

      {/* 游戏列表 */}
      <div className="section">
        <div className="filter-bar">
          <h3>浏览游戏</h3>
          <div className="filter-buttons">
            {['waiting', 'ready', 'in_progress'].map(s => (
              <button
                key={s}
                className={`btn-filter ${filter === s ? 'active' : ''}`}
                onClick={() => setFilter(s)}
              >{STATUS_LABEL[s]}</button>
            ))}
            <button className="btn-filter" onClick={fetchGames} title="刷新">🔄</button>
          </div>
        </div>

        {loading ? (
          <p className="hint">加载中...</p>
        ) : (
          <div className="game-list">
            {games.length === 0 && (
              <p className="hint">暂无符合条件的游戏，创建一个吧！</p>
            )}
            {games.map(g => {
              const alreadyIn = myGames.some(mg => mg.id === g.id);
              return (
                <div key={g.id} className="game-card">
                  <div className="game-card-title">
                    <strong>房间 #{g.id}</strong>
                    <span className={`status-badge status-${g.status}`}>{STATUS_LABEL[g.status]}</span>
                  </div>
                  <div className="game-card-info">
                    {g.players.map(p => p.username).join('、')} &nbsp;
                    ({g.players.length}/{g.max_players} 人)
                  </div>
                  {alreadyIn ? (
                    <button className="btn-secondary" onClick={() => handleResume(g)}>
                      {g.status === 'in_progress' ? '继续' : '进入房间'}
                    </button>
                  ) : (
                    g.status === 'waiting' && (
                      <button className="btn-primary" onClick={() => handleJoin(g.id)}>加入</button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
